import { useEffect, useRef, useState, type ReactElement } from "react";
import { NorthCompass } from "./NorthCompass";
import { CAMERA_PRESET_ITEMS } from "../inspector/CameraLayersCard";
import { getInspectorDrawerControls } from "../inspector/inspectorDrawerControlsBridge";
import styles from "./CameraCompassMenu.module.css";

/**
 * The top-nav compass doubles as the camera-presets entry point: clicking
 * it drops a small preset menu (Top Down, Road Approach, …). Preset
 * activation reaches the scene through the inspector drawer controls
 * bridge — the same `details.onCameraPreset` handler the mobile drawer
 * uses — so this React root never touches `main.ts` directly.
 */
export function CameraCompassMenu(): ReactElement {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={styles.root}>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Camera views"
        title="Camera views"
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        <NorthCompass />
      </button>
      {open ? (
        <div className={styles.menu} role="menu" aria-label="Camera presets">
          {CAMERA_PRESET_ITEMS.map((item) => (
            <button
              key={item.preset}
              type="button"
              role="menuitem"
              className={styles.menuItem}
              onClick={() => {
                setOpen(false);
                getInspectorDrawerControls().details.onCameraPreset(
                  item.preset
                );
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
